import { memo } from 'react';
import { useCalendarGridStore } from '../../store/calendar-grid.store';
import { GroupBody } from './group-body';
import { GroupHeader } from './group-header';
import { GroupHourLines } from './group-hour-lines';
import { TodayTimeIndicator } from './time-indicator/today-time-indicator';

// Renders column headers — each group manages its own DndContext internally via GroupHeaderDndWrapper
export const ColumnGroupHeaders = memo(() => {
  const { groupIds } = useCalendarGridStore(['groupIds']);
  if (!groupIds.length) return null;
  return groupIds.map((groupId, index) => (
    <ColumnGroupSectionHeader key={groupId} groupId={groupId} groupIndex={index} />
  ));
});
ColumnGroupHeaders.displayName = 'ColumnGroupHeaders';

// Renders column bodies, hour lines, and time indicators — must live inside EventDragDropContext
export const ColumnGroupBodies = memo(() => {
  const { groupIds } = useCalendarGridStore(['groupIds']);
  if (!groupIds.length) return null;
  return groupIds.map((groupId, index) => (
    <ColumnGroupSectionBody key={groupId} groupId={groupId} groupIndex={index} />
  ));
});
ColumnGroupBodies.displayName = 'ColumnGroupBodies';

const useColumnGroupSlice = (groupId: string, groupIndex: number) => {
  const { hasMultipleGroups, headerType, columnBodyVariant, localColumnOrderByGroup } = useCalendarGridStore([
    'hasMultipleGroups',
    'headerType',
    'columnBodyVariant',
    'localColumnOrderByGroup',
  ]);
  const groupInfo = useCalendarGridStore((s) => s.groupsById[groupId]);
  if (!groupInfo?.columnIds?.length) return null;
  const columnIds = localColumnOrderByGroup[groupId] ?? groupInfo.columnIds;
  const startColumn = groupIndex + groupInfo.groupOffset + 2;
  const gridColumn = `${startColumn} / span ${groupInfo.columnIds.length}`;
  return { groupInfo, columnIds, startColumn, gridColumn, hasMultipleGroups, headerType, columnBodyVariant };
};

const ColumnGroupSectionHeader = ({ groupId, groupIndex }: { groupId: string; groupIndex: number }) => {
  const slice = useColumnGroupSlice(groupId, groupIndex);
  if (!slice) return null;
  const { groupInfo, columnIds, gridColumn, hasMultipleGroups, headerType } = slice;
  return (
    <GroupHeader
      gridColumn={gridColumn}
      groupInfo={groupInfo}
      isGroupHeaderVisible={hasMultipleGroups}
      headerType={headerType}
      columnIds={columnIds}
    />
  );
};

const ColumnGroupSectionBody = ({ groupId, groupIndex }: { groupId: string; groupIndex: number }) => {
  const slice = useColumnGroupSlice(groupId, groupIndex);
  if (!slice) return null;
  const { groupInfo, columnIds, startColumn, gridColumn, columnBodyVariant } = slice;
  return (
    <>
      <GroupBody
        isFirstGroup={groupIndex === 0}
        startColumn={startColumn}
        groupId={groupId}
        columnIds={columnIds}
        columnBodyVariant={columnBodyVariant}
      />
      <GroupHourLines />
      <TodayTimeIndicator gridColumn={gridColumn} timezone={groupInfo.timezone} />
    </>
  );
};
